import { memo } from 'react';
import styled from '@emotion/styled';

import { StyledButton, StyledInput, StyledTitle } from '@selfserve/react-ui';
import { sortingOrder } from '@selfserve/react-utils';

export const StyledTable = memo(
  styled('table')(
    ({ theme, withoutMargin }: { theme?: any; withoutMargin?: boolean }) => ({
      minWidth: '100%',
      width: 'max-content',
      ...(!withoutMargin && {
        marginTop: '24px',
      }),
    })
  )
);

export const StyledTh = memo(
  styled('th')(({ theme, isButton }: { theme?: any; isButton?: boolean }) => ({
    textAlign: 'left',
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    padding: '16px 16px 10px',
    color: theme.palette.grey[300],
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    cursor: 'default',
    '&:first-of-type': {
      paddingLeft: '24px',
      color: theme.palette.grey[100],
    },
    '&:last-of-type': {
      paddingLeft: '124px',
    },
    ...(isButton && {
      cursor: 'pointer',
      '&::after': {
        display: 'inline-block',
        content: '""',
        background: 'no-repeat url("assets/dropdown-arrow.svg") center',
        width: '8px',
        height: '8px',
        marginLeft: '8px',
      },
      [`&.${sortingOrder.ASC}`]: {
        '&::after': {
          transform: 'rotate(180deg)',
        },
      },
    }),
  }))
);

export const StyledTd = memo(
  styled('td')(
    ({ theme, isPairValue }: { theme?: any; isPairValue?: boolean }) => ({
      borderBottom: `1px solid ${theme.palette.grey[500]}`,
      padding: '16px',
      lineHeight: '18px',
      color: theme.palette.grey[300],
      fontWeight: 700,
      ...(isPairValue && { fontWeight: 500 }),
      fontSize: theme.typography.pxToRem(14),
      '&:first-of-type': {
        paddingLeft: '24px',
        color: theme.palette.grey[100],
      },
      '&:last-of-type': {
        paddingLeft: '124px',
      },
      span: {
        color: theme.palette.grey[300],
        display: 'block',
      },
    })
  )
);

export const StyledTr = memo(
  styled('tr')(
    ({
      theme,
      disabled,
      sticky,
    }: {
      theme?: any;
      disabled?: boolean;
      sticky?: boolean;
    }) => ({
      position: 'relative',
      cursor: disabled ? 'initial' : 'pointer',
      ...(sticky && {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: '#FFF',
      }),
    })
  )
);

export const StyledPageTitle = memo(styled(StyledTitle)`
  magin-bottom: 38px;
  text-transform: capitalize;
`);

export const StyledSearchInput = memo(
  styled(StyledInput)(({ theme, width }: { theme?: any; width?: number }) => ({
    height: '32px',
    width: `${width || '200'}px`,
    background: 'no-repeat url("assets/search.svg")',
    backgroundPositionY: 'center',
    backgroundPositionX: '12px',
    '.kepuikit-base-input-input': {
      padding: '0 8px 0 36px',
    },
  }))
);

export const StyledStatus = memo(
  styled('p')(
    ({
      theme,
      useDefaultTextStyles,
      selected,
      withMargin,
    }: {
      theme?: any;
      useDefaultTextStyles?: boolean;
      selected?: boolean;
      withMargin?: boolean;
    }) => ({
      display: 'flex',
      alignItems: 'center',
      margin: 0,
      ...(withMargin && {
        margin: `0 0 0 ${theme.spacing(1)}`,
      }),
      '&::before': {
        display: 'inline-block',
        content: '""',
        backgroundColor: theme.palette.grey[300],
        width: '8px',
        height: '8px',
        borderRadius: '4px',
        marginRight: '8px',
      },
      '&.red': {
        color: theme.palette.danger.main,
        '&::before': {
          backgroundColor: theme.palette.danger.main,
        },
      },
      '&.green': {
        color: '#00A078',
        '&::before': {
          backgroundColor: '#00A078',
        },
      },
      '&.yellow': {
        color: '#FFAA00',
        '&::before': {
          backgroundColor: '#FFAA00',
        },
      },
      ...(useDefaultTextStyles && {
        color: theme.palette.grey[100],
        fontWeight: '600',
        '&::before': {
          display: 'none',
        },
      }),
      ...(typeof selected === 'boolean' && {
        position: 'relative',
        padding: '10px 0',
        width: '114px',
      }),
      ...(selected && {
        '&::after': {
          content: '""',
          position: 'absolute',
          background: 'no-repeat url("assets/default-check.svg") center',
          width: '9px',
          height: '8px',
          right: '0',
          top: '50%',
          marginTop: '-4px',
        },
      }),
    })
  )
);

export const StyledType = memo(
  styled('p')(
    ({
       theme,
       useDefaultTextStyles,
       selected,
       withMargin,
     }: {
      theme?: any;
      useDefaultTextStyles?: boolean;
      selected?: boolean;
      withMargin?: boolean;
    }) => ({
      display: 'flex',
      alignItems: 'center',
      margin: 0,
      ...(withMargin && {
        margin: `0 0 0 ${theme.spacing(1)}`,
      }),
      '&::before': {
        display: 'inline-block',
        content: '""',
        backgroundColor: theme.palette.grey[300],
        width: '8px',
        height: '8px',
        borderRadius: '4px',
        marginRight: '8px',
      },
      '&.red': {
        color: theme.palette.danger.main,
        '&::before': {
          backgroundColor: theme.palette.danger.main,
        },
      },
      '&.green': {
        color: '#00A078',
        '&::before': {
          backgroundColor: '#00A078',
        },
      },
      '&.yellow': {
        color: '#FFAA00',
        '&::before': {
          backgroundColor: '#FFAA00',
        },
      },
      ...(useDefaultTextStyles && {
        color: theme.palette.grey[100],
        fontWeight: '600',
        '&::before': {
          display: 'none',
        },
      }),
      ...(typeof selected === 'boolean' && {
        position: 'relative',
        padding: '10px 0',
        width: '114px',
      }),
      ...(selected && {
        '&::after': {
          content: '""',
          position: 'absolute',
          background: 'no-repeat url("assets/default-check.svg") center',
          width: '9px',
          height: '8px',
          right: '0',
          top: '50%',
          marginTop: '-4px',
        },
      }),
    })
  )
);

export const StyledExportButton = styled(StyledButton, {
  shouldForwardProp: (prop) => {
    return prop !== 'withoutAutoMargin';
  },
})(
  ({ theme, withoutAutoMargin }: { theme?: any, withoutAutoMargin?: boolean }) => ({
    position: 'relative',
    width: '90px',
    height: '32px',
    fontSize: '14px',
    justifyContent: 'flex-end',
    padding: '0 10px',
    ...(!withoutAutoMargin && { marginLeft: 'auto' }),
    '&:before': {
      position: 'absolute',
      content: '""',
      background: 'no-repeat url("assets/export.svg") left center',
      width: '18px',
      height: '17px',
      left: '10px',
      top: '50%',
      marginTop: '-9px',
    },
  })
);

export const StyledControlledListContainer = styled.div`
  position: relative;
  width: 100%;
`;
